import React from 'react';

import { Checkbox, Typography, Tooltip, message } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  ACTIONS,
  RESOURCES_BY_ROLE,
  RESOURCE_ACTIONS_BY_ROLE,
} from '../../../permissions_manifest.generated';
import { canGrant } from '@utils/permissionSubset';

const { Text } = Typography;

type Role = 'admin' | 'operator';
type DiffStatus = 'added' | 'removed' | null;

interface PermissionMatrixProps {
  /** 當前已選權限列表 */
  value?: string[];
  /** 權限變更回調 */
  onChange: (permissions: string[]) => void;
  /** 'admin' 或 'operator' */
  role?: Role;
  /** 編輯自己時保護特定權限 */
  isEditingSelf?: boolean;
  /** 提供時顯示與此基準的 diff 顏色 */
  diffSource?: string[] | null;
  /**
   * If provided, cells for permissions not grantable by this perm set are
   * disabled (prevents submitting grants that will be rejected by the backend
   * subset guard).
   */
  grantableBy?: string[] | null;
}

/**
 * PermissionMatrix - 純 checkbox 權限矩陣（受控組件）
 *
 * 從 PermissionsEditor 提取的純展示 + 互動矩陣，不含 template 選擇、
 * 儲存/重設/清除按鈕、摘要面板等外部邏輯。
 */
const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  value = [],
  onChange,
  role = 'admin',
  isEditingSelf = false,
  diffSource = null,
  grantableBy = null,
}) => {
  const { t } = useTranslation();

  const availableResources =
    ((RESOURCES_BY_ROLE as Record<string, readonly string[]>)[role] ?? []) as readonly string[] as string[];
  const availableActions = ACTIONS as readonly string[] as string[];
  const resourceActions =
    ((RESOURCE_ACTIONS_BY_ROLE as Record<string, Record<string, readonly string[]>>)[role] ?? {}) as Record<string, readonly string[]>;

  const getResourceActions = (resource: string): readonly string[] => {
    return resourceActions[resource] || ['read', 'write'];
  };

  const getActionResources = (action: string): string[] => {
    return availableResources.filter(r => getResourceActions(r).includes(action));
  };

  // 檢查是否為受保護權限（不可移除）
  const isProtectedPermission = (permission: string): boolean => {
    if (!isEditingSelf) return false;
    if (role !== 'operator') return false;

    const protectedPermissions = ['operators:write', 'operators:read'];
    return protectedPermissions.includes(permission);
  };

  // 檢查是否為當前 granter 無權授予的權限
  // grantableBy === null 表示無限制（向下相容）
  const isUngrantable = (permission: string): boolean => {
    if (grantableBy === null) return false;
    return !canGrant(grantableBy, permission, {
      resources: availableResources,
      resourceActions,
      allActions: availableActions,
    });
  };

  // 獲取權限說明文字 (Tooltip 用)
  const getTooltipText = (resource: string, action: string): React.ReactNode => {
    const resourceName = t(`common.resources.${resource}`, resource) as string;
    return t(`common.permission_tooltips.${action}`, { resource: resourceName }) as string;
  };

  // 檢查權限是否被勾選（考慮父權限） - 基於 value prop
  const isPermissionChecked = (permission: string): boolean => {
    if (value.includes(permission)) return true;
    if (value.includes('*:*')) return true;

    const [resource, action] = permission.split(':');

    if (resource !== '*' && value.includes(`${resource}:*`)) return true;
    if (action !== '*' && value.includes(`*:${action}`)) return true;

    if (action === '*' && resource !== '*') {
      const supported = getResourceActions(resource);
      if (supported.every(a =>
        value.includes(`${resource}:${a}`) ||
        value.includes(`*:${a}`)
      )) {
        return true;
      }
    }

    return false;
  };

  // 檢查權限在 diffSource 中是否被勾選
  const isDiffSourceChecked = (permission: string): boolean => {
    if (!diffSource) return false;

    if (diffSource.includes(permission)) return true;
    if (diffSource.includes('*:*')) return true;

    const [resource, action] = permission.split(':');
    if (resource !== '*' && diffSource.includes(`${resource}:*`)) return true;
    if (action !== '*' && diffSource.includes(`*:${action}`)) return true;

    if (action === '*' && resource !== '*') {
      const supported = getResourceActions(resource);
      if (supported.every(a =>
        diffSource.includes(`${resource}:${a}`) ||
        diffSource.includes(`*:${a}`)
      )) {
        return true;
      }
    }

    return false;
  };

  // 取得某個權限的 diff 狀態：'added' | 'removed' | null
  const getPermissionDiff = (permission: string): DiffStatus => {
    if (!diffSource) return null;

    const baseChecked = isDiffSourceChecked(permission);
    const currentChecked = isPermissionChecked(permission);
    if (!baseChecked && currentChecked) return 'added';
    if (baseChecked && !currentChecked) return 'removed';
    return null;
  };

  // 處理權限勾選/取消（呼叫 onChange 回調）
  const handlePermissionToggle = (permission: string): void => {
    if (isProtectedPermission(permission)) {
      message.warning(t('common.permissions_editor.cannot_remove_self') as string);
      return;
    }

    let newPermissions: string[];

    if (isPermissionChecked(permission)) {
      // 取消勾選：需要先展開父權限，再移除該權限
      if (value.includes('*:*')) {
        // 展開 *:* 時要依 manifest 的 resource_actions 過濾，避免冒出
        // 例如 brands:read（manifest 上 brands 只允許 write）這類 BE 會 reject 的字串
        const allPermissions = new Set<string>();
        availableResources.forEach(resource => {
          allPermissions.add(`${resource}:*`);
          getResourceActions(resource).forEach(action => {
            allPermissions.add(`${resource}:${action}`);
          });
        });
        newPermissions = Array.from(allPermissions);
      } else {
        newPermissions = [...value];
      }

      newPermissions = newPermissions.filter(p => p !== permission);

      if (permission.includes(':') && !permission.startsWith('*:') && !permission.endsWith(':*')) {
        const [resource, action] = permission.split(':');
        newPermissions = newPermissions.filter(p => p !== `${resource}:*`);
        newPermissions = newPermissions.filter(p => p !== '*:*');
        newPermissions = newPermissions.filter(p => p !== `*:${action}`);
      }

      if (permission === '*:*') {
        newPermissions = newPermissions.filter(p => isProtectedPermission(p));
      } else if (permission.startsWith('*:') && permission !== '*:*') {
        const action = permission.split(':')[1];
        newPermissions = newPermissions.filter(p => {
          if (isProtectedPermission(p)) return true;
          return !p.endsWith(`:${action}`) || p.startsWith('*:');
        });
      } else if (permission.endsWith(':*') && !permission.startsWith('*:')) {
        const resource = permission.split(':')[0];
        newPermissions = newPermissions.filter(p => {
          if (isProtectedPermission(p)) return true;
          return !p.startsWith(`${resource}:`);
        });
      }
    } else {
      newPermissions = [...value, permission];

      if (permission === '*:*') {
        // *:* 展開時依 manifest 過濾每個 resource 支援的 actions，避免送出
        // brands:read / logs:write 這類 manifest 不允許的組合
        const allPermissions = new Set<string>(newPermissions);
        availableResources.forEach(resource => {
          allPermissions.add(`${resource}:*`);
          getResourceActions(resource).forEach(action => {
            allPermissions.add(`${resource}:${action}`);
          });
        });
        availableActions.forEach(action => {
          allPermissions.add(`*:${action}`);
        });
        newPermissions = Array.from(allPermissions);
      } else if (permission.startsWith('*:') && permission !== '*:*') {
        // *:action 整欄展開時只 union 真的支援這 action 的 resources
        // 例如 *:approve 只 add 給 inventories / purchase_orders，不能 add 給 brands / logs
        const action = permission.split(':')[1];
        const allPermissions = new Set<string>(newPermissions);
        getActionResources(action).forEach(resource => {
          allPermissions.add(`${resource}:${action}`);
        });
        newPermissions = Array.from(allPermissions);
      } else if (permission.endsWith(':*') && !permission.startsWith('*:')) {
        const resource = permission.split(':')[0];
        const allPermissions = new Set<string>(newPermissions);
        getResourceActions(resource).forEach(action => {
          allPermissions.add(`${resource}:${action}`);
        });
        newPermissions = Array.from(allPermissions);
      }
    }

    onChange(newPermissions);
  };

  // Column 全選/取消：根據 column 視覺狀態決定 check 或 uncheck，
  // 因為 isPermissionChecked('*:action') 不會從個別 cell 推導出 true。
  const handleColumnToggle = (action: string): void => {
    const actionResources = getActionResources(action);
    if (actionResources.length === 0) return;

    const allChecked = actionResources.every(r => isPermissionChecked(`${r}:${action}`));

    if (!allChecked) {
      handlePermissionToggle(`*:${action}`);
      return;
    }

    // Uncheck path: 展開 *:* 與 resource:* 為個別權限後，移除整欄
    const expanded = new Set<string>(value);

    if (expanded.has('*:*')) {
      expanded.delete('*:*');
      availableResources.forEach(r => {
        expanded.add(`${r}:*`);
        getResourceActions(r).forEach(a => expanded.add(`${r}:${a}`));
      });
      availableActions.forEach(a => expanded.add(`*:${a}`));
    }

    availableResources.forEach(r => {
      if (expanded.has(`${r}:*`)) {
        expanded.delete(`${r}:*`);
        getResourceActions(r).forEach(a => expanded.add(`${r}:${a}`));
      }
    });

    expanded.delete(`*:${action}`);
    actionResources.forEach(r => {
      const perm = `${r}:${action}`;
      if (isProtectedPermission(perm)) return;
      expanded.delete(perm);
    });

    onChange(Array.from(expanded));
  };

  return (
    <div>
      {/* Diff CSS - 僅在有 diffSource 時渲染 */}
      {diffSource && (
        <style>{`
          .perm-diff-added .ant-checkbox-checked .ant-checkbox-inner {
            background-color: #52c41a !important;
            border-color: #52c41a !important;
          }
          .perm-diff-removed .ant-checkbox-checked .ant-checkbox-inner {
            background-color: #ff4d4f !important;
            border-color: #ff4d4f !important;
          }
          .perm-diff-removed .ant-checkbox-checked .ant-checkbox-inner::after {
            content: '';
            display: block;
            width: 8px;
            height: 0;
            border: none !important;
            border-top: 2px solid #fff !important;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) !important;
          }
        `}</style>
      )}

      {/* 功能權限矩陣 - 表格形式 */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
              <th style={{ textAlign: 'left', padding: '12px 8px', minWidth: '180px' }}>
                {t('common.permission_labels.resources') as string}
              </th>
              {availableActions.map(action => {
                const actionResources = getActionResources(action);
                const checkedCells = actionResources.filter(r =>
                  isPermissionChecked(`${r}:${action}`)
                );
                const allChecked = actionResources.length > 0 &&
                  checkedCells.length === actionResources.length;
                const indeterminate = checkedCells.length > 0 && !allChecked;
                const colUngrantable = isUngrantable(`*:${action}`);
                const colTooltip = colUngrantable
                  ? (t('common.permissions_editor.cannot_grant_exceeding') as string)
                  : `${t('common.permission_actions.*', '全選') as string}：${t(`common.permission_actions.${action}`, action) as string}`;

                return (
                  <th key={action} style={{ textAlign: 'center', padding: '12px 8px', minWidth: '70px' }}>
                    <div
                      style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                        lineHeight: 1.2,
                      }}
                    >
                      <Text strong>{t(`common.permission_actions.${action}`, action) as string}</Text>
                      <Tooltip title={colTooltip}>
                        <Checkbox
                          checked={allChecked}
                          indeterminate={indeterminate}
                          onChange={() => handleColumnToggle(action)}
                          disabled={colUngrantable || actionResources.length === 0}
                        />
                      </Tooltip>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {availableResources.map((resource, index) => {
              return (
                <tr
                  key={resource}
                  style={{
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff'
                  }}
                >
                  {/* 功能名稱（含全選 checkbox） */}
                  <td style={{ padding: '10px 8px' }}>
                    {(() => {
                      const rowWildcardUngrantable = isUngrantable(`${resource}:*`);
                      const rowTooltip = rowWildcardUngrantable
                        ? (t('common.permissions_editor.cannot_grant_exceeding') as string)
                        : getTooltipText(resource, 'all');
                      return (
                        <Tooltip title={rowTooltip}>
                          <Checkbox
                            checked={isPermissionChecked(`${resource}:*`)}
                            onChange={() => handlePermissionToggle(`${resource}:*`)}
                            disabled={rowWildcardUngrantable}
                          >
                            <Text strong>{t(`common.resources.${resource}`, resource) as string}</Text>
                          </Checkbox>
                        </Tooltip>
                      );
                    })()}
                  </td>
                  {/* 各別動作 */}
                  {availableActions.map(action => {
                    const supported = getResourceActions(resource).includes(action);

                    if (!supported) {
                      return (
                        <td key={action} style={{ textAlign: 'center', padding: '10px 8px', color: '#d9d9d9' }}>
                          —
                        </td>
                      );
                    }

                    const permission = `${resource}:${action}`;
                    const protectedPerm = isProtectedPermission(permission);
                    const ungrantable = isUngrantable(permission);
                    const disabled = protectedPerm || ungrantable;
                    const diff = getPermissionDiff(permission);

                    const showChecked = diff
                      ? true
                      : (isPermissionChecked(permission) || protectedPerm);
                    const tooltipTitle = ungrantable
                      ? (t('common.permissions_editor.cannot_grant_exceeding') as string)
                      : getTooltipText(resource, action);

                    const diffClass = diff === 'added'
                      ? 'perm-diff-added'
                      : diff === 'removed'
                        ? 'perm-diff-removed'
                        : '';

                    return (
                      <td
                        key={action}
                        style={{ textAlign: 'center', padding: '10px 8px' }}
                      >
                        <Tooltip title={tooltipTitle}>
                          <span className={diffClass}>
                            <Checkbox
                              checked={showChecked}
                              onChange={() => handlePermissionToggle(permission)}
                              disabled={disabled}
                            />
                          </span>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PermissionMatrix;
